import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp,
  ShoppingCart,
  DollarSign,
  Users,
  Calendar,
  BarChart3,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { cn, formatCurrency, formatNumber, formatDate, calculatePercentage } from '@utils';
import api from '@services/api';
import { ProgressBar } from '../../components/ui/ProgressBar';

interface SalesData {
  dailySales: Array<{ date: string; orders: number; revenue: number }>;
  totalRevenue: number;
  totalOrders: number;
  averageOrderValue: number;
}

interface AttendanceEvent {
  id: number;
  name: string;
  startDate: string;
  capacity: number;
  soldCount: number;
  totalTickets: number;
  checkedIn: number;
}

export default function ReportsPage() {
  const [days, setDays] = useState('30');

  const { data: salesData, isLoading: salesLoading } = useQuery({
    queryKey: ['reports-sales', days],
    queryFn: () => api.get<{ data: SalesData }>('/reports/sales', { days }),
  });

  const { data: attendanceData, isLoading: attendanceLoading } = useQuery({
    queryKey: ['reports-attendance'],
    queryFn: () => api.get<{ data: AttendanceEvent[] }>('/reports/attendance'),
  });

  const sales = salesData?.data;
  const events = attendanceData?.data || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reports Overview</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Track sales, revenue, and attendance metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(e.target.value)}
            className="input py-2"
            aria-label="Select date range"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <Link to="/reports/sales" className="btn-primary">
            <BarChart3 className="w-4 h-4" />
            Sales Report
          </Link>
        </div>
      </div>

      {/* KPI Cards */}
      {salesLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="card p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/2 dark:bg-gray-700 mb-3" />
              <div className="h-8 bg-gray-200 rounded w-3/4 dark:bg-gray-700" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="card p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {formatCurrency(sales?.totalRevenue || 0)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-success-50 dark:bg-success-900/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-success-600 dark:text-success-400" />
              </div>
            </div>
          </div>
          <div className="card p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {formatNumber(sales?.totalOrders || 0)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
            </div>
          </div>
          <div className="card p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Order Value</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {formatCurrency(sales?.averageOrderValue || 0)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-warning-50 dark:bg-warning-900/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-warning-600 dark:text-warning-400" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attendance Table */}
      <div className="card overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Users className="w-5 h-5" />
            Event Attendance
          </h3>
        </div>
        <div className="table-container">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Event</th>
                <th className="table-header-cell">Date</th>
                <th className="table-header-cell">Capacity</th>
                <th className="table-header-cell">Tickets</th>
                <th className="table-header-cell">Checked In</th>
                <th className="table-header-cell">Check-in Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {attendanceLoading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="table-row">
                    {[...Array(6)].map((_, j) => (
                      <td key={j} className="table-cell">
                        <div className="h-4 bg-gray-200 rounded animate-pulse dark:bg-gray-700" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : events.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center">
                    <div className="empty-state">
                      <Calendar className="empty-state-icon" />
                      <h3 className="empty-state-title">No events yet</h3>
                      <p className="empty-state-description">Attendance data will appear once you have events.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                events.map((event) => {
                  const checkinRate = event.totalTickets > 0
                    ? calculatePercentage(event.checkedIn, event.totalTickets)
                    : 0;
                  return (
                    <tr key={event.id} className="table-row">
                      <td className="table-cell">
                        <Link
                          to={`/events/${event.id}`}
                          className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
                        >
                          {event.name}
                        </Link>
                      </td>
                      <td className="table-cell">{formatDate(event.startDate)}</td>
                      <td className="table-cell">{formatNumber(event.capacity)}</td>
                      <td className="table-cell">{formatNumber(event.totalTickets)}</td>
                      <td className="table-cell">{formatNumber(event.checkedIn)}</td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <div className="w-16">
                            <ProgressBar percentage={Math.min(checkinRate, 100)} />
                          </div>
                          <span className="text-sm">{checkinRate}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
}
